import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Autocomplete,
  Alert,
  Divider,
  MenuItem,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
} from "@mui/material";
import {
  Search,
  Warning,
  CheckCircle,
  Restore,
  Delete,
  Receipt,
} from "@mui/icons-material";
import { itemIssueService } from "../../services/itemIssueService";
import itemIssueReturnService from "../../services/itemIssueReturnService";
import { useSnackbar } from "notistack";

export const ItemIssueReturnForm = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const issueIdFromUrl = searchParams.get("issueId");

  const [itemIssues, setItemIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [returnableItems, setReturnableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [issueLoading, setIssueLoading] = useState(false);
  const [returnableLoading, setReturnableLoading] = useState(false);

  // Issue search state
  const [issueSearchTerm, setIssueSearchTerm] = useState("");

  // Initial data loading
  useEffect(() => {
    if (issueIdFromUrl) {
      fetchIssueById(issueIdFromUrl);
    } else {
      fetchItemIssues();
    }
  }, [issueIdFromUrl]);

  // Debounced issue search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (issueSearchTerm.trim() !== "") {
        fetchItemIssues();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [issueSearchTerm]);

  const fetchIssueById = async (issueId) => {
    try {
      setIssueLoading(true);
      const response = await itemIssueService.getItemIssue(issueId);
      const issue = response.data.data.itemIssue;

      if (issue.status !== "issued") {
        enqueueSnackbar("Only issued item issues can be returned", {
          variant: "warning",
        });
        return;
      }

      setSelectedIssue(issue);
      await fetchReturnableItems(issueId);
    } catch (error) {
      enqueueSnackbar("Error fetching item issue", { variant: "error" });
    } finally {
      setIssueLoading(false);
    }
  };

  const fetchItemIssues = async () => {
    try {
      setIssueLoading(true);
      const params = {
        status: "issued",
        ...(issueSearchTerm && { search: issueSearchTerm }),
        limit: 20,
      };

      const response = await itemIssueService.getItemIssues(params);
      setItemIssues(response.data.data.itemIssues || []);
    } catch (error) {
      enqueueSnackbar("Error fetching item issues", { variant: "error" });
    } finally {
      setIssueLoading(false);
    }
  };

  const fetchReturnableItems = async (issueId) => {
    try {
      setReturnableLoading(true);
      const response = await itemIssueService.getReturnableItems(issueId);
      const items = response.data.data.returnableItems || [];

      if (items.length === 0) {
        enqueueSnackbar("No returnable items found for this issue", {
          variant: "info",
        });
      }

      setReturnableItems(items);
    } catch (error) {
      enqueueSnackbar("Error fetching returnable items", { variant: "error" });
      setReturnableItems([]);
    } finally {
      setReturnableLoading(false);
    }
  };

  const handleIssueChange = async (event, newValue) => {
    setSelectedIssue(newValue);
    setSelectedItems([]);
    setReturnableItems([]);

    if (newValue) {
      await fetchReturnableItems(newValue._id);
    }
  };

  const handleItemSelect = (returnableItem, isSelected) => {
    if (isSelected) {
      setSelectedItems([
        ...selectedItems,
        {
          issueItem: returnableItem.originalItemId,
          item: returnableItem.item._id,
          batchStock: returnableItem.batchStock._id,
          quantity: 1,
          originalQuantity: returnableItem.issuedQuantity,
          costPrice: returnableItem.costPrice,
          reason: "other",
          description: "",
          restockable: true,
          disposalRequired: false,
          // Store display info
          itemName: returnableItem.item.name,
          itemBrand: returnableItem.item.brand,
          batchNumber: returnableItem.batchStock.batchNumber,
          expiryDate: returnableItem.batchStock.expiryDate,
          maxQuantity: returnableItem.remainingQuantity,
        },
      ]);
    } else {
      setSelectedItems(
        selectedItems.filter(
          (item) => item.issueItem !== returnableItem.originalItemId
        )
      );
    }
  };

  // const updateSelectedItem = (issueItemId, field, value) => {
  //   setSelectedItems(
  //     selectedItems.map((item) =>
  //       item.issueItem === issueItemId ? { ...item, [field]: value } : item
  //     )
  //   );
  // };
  const updateSelectedItem = (issueItemId, field, value) => {
    setSelectedItems(
      selectedItems.map((item) => {
        if (item.issueItem === issueItemId) {
          const updatedItem = { ...item, [field]: value };

          // Smart auto-set based on reason
          if (field === "reason") {
            switch (value) {
              case "defective":
              case "expired":
                updatedItem.restockable = false;
                updatedItem.disposalRequired = true;
                break;
              case "excess_quantity":
              case "wrong_item":
              case "project_cancelled":
              case "other":
              default:
                updatedItem.restockable = true;
                updatedItem.disposalRequired = false;
                break;
            }
          }

          // Ensure mutual exclusivity when manually toggling
          if (field === "restockable" && value === true) {
            updatedItem.disposalRequired = false;
          }
          if (field === "disposalRequired" && value === true) {
            updatedItem.restockable = false;
          }

          return updatedItem;
        }
        return item;
      })
    );
  };

  const calculateTotals = () => {
    const subtotal = selectedItems.reduce((sum, item) => {
      return sum + item.quantity * item.costPrice;
    }, 0);

    const tax = subtotal * 0.18;
    const totalAmount = subtotal + tax;

    return { subtotal, tax, totalAmount };
  };

  const validateForm = () => {
    if (!selectedIssue) {
      enqueueSnackbar("Please select an item issue", { variant: "warning" });
      return false;
    }

    if (selectedItems.length === 0) {
      enqueueSnackbar("Please select at least one item to return", {
        variant: "warning",
      });
      return false;
    }

    if (!reason.trim()) {
      enqueueSnackbar("Please provide a reason for the return", {
        variant: "warning",
      });
      return false;
    }

    // Validate quantities
    for (const item of selectedItems) {
      if (item.quantity < 1) {
        enqueueSnackbar(`Invalid quantity for ${item.itemName}`, {
          variant: "warning",
        });
        return false;
      }
      if (item.quantity > item.maxQuantity) {
        enqueueSnackbar(
          `Quantity for ${item.itemName} exceeds maximum returnable quantity`,
          { variant: "warning" }
        );
        return false;
      }
      if (!item.reason) {
        enqueueSnackbar(`Please select a reason for ${item.itemName}`, {
          variant: "warning",
        });
        return false;
      }
      if (!item.restockable && !item.disposalRequired) {
        enqueueSnackbar(
          `Please select disposition for ${item.itemName}: either Restockable or Disposal Required`,
          { variant: "warning" }
        );
        return false;
      }

      if (item.restockable && item.disposalRequired) {
        enqueueSnackbar(
          `${item.itemName} cannot be both restockable and require disposal. Please select only one.`,
          { variant: "warning" }
        );
        return false;
      }

    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const returnData = {
        itemIssue: selectedIssue._id,
        department: selectedIssue.department._id,
        items: selectedItems.map((item) => ({
          issueItem: item.issueItem,
          item: item.item,
          batchStock: item.batchStock,
          quantity: item.quantity,
          originalQuantity: item.originalQuantity,
          costPrice: item.costPrice,
          reason: item.reason,
          description: item.description || undefined,
          restockable: item.restockable,
          disposalRequired: item.disposalRequired,
        })),
        reason,
        notes: notes || undefined,
      };

      const response = await itemIssueReturnService.createItemIssueReturn(
        returnData
      );

      enqueueSnackbar("Item issue return created successfully!", {
        variant: "success",
      });

      const returnRecord = response.data.data;
      enqueueSnackbar(
        `Return #${returnRecord.returnNumber} created. Status: ${returnRecord.status}`,
        { variant: "info", autoHideDuration: 5000 }
      );

      // Reset form
      setSelectedIssue(null);
      setReturnableItems([]);
      setSelectedItems([]);
      setReason("");
      setNotes("");

      setTimeout(() => {
        navigate("/hospital/hospital-inventory/item-issue-returns");
      }, 2000);
    } catch (error) {
      let errorMessage = "Error creating item issue return";

      if (error.response) {
        const backendError = error.response.data;
        errorMessage = backendError.message || errorMessage;
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      } else {
        errorMessage = error.message || errorMessage;
      }

      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 5000,
      });

      console.error("Submit error:", error);
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, tax, totalAmount } = calculateTotals();

  const isItemSelected = (issueItemId) => {
    return selectedItems.some((item) => item.issueItem === issueItemId);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        New Item Issue Return
      </Typography>

      <Grid container spacing={3}>
        {/* Left Column - Issue Selection & Items */}
        <Grid item xs={12} md={8}>
          {/* Issue Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Select Item Issue
              </Typography>

              <Autocomplete
                options={itemIssues}
                getOptionLabel={(option) =>
                  `${option.issueNumber} - ${
                    option.department?.department || "N/A"
                  } (${new Date(option.issueDate).toLocaleDateString()})`
                }
                value={selectedIssue}
                onChange={handleIssueChange}
                inputValue={issueSearchTerm}
                onInputChange={(event, newValue) =>
                  setIssueSearchTerm(newValue)
                }
                loading={issueLoading}
                disabled={!!issueIdFromUrl}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Item Issue"
                    placeholder="Search by issue number or department"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Search sx={{ mr: 1, color: "text.secondary" }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {issueLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {selectedIssue && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Issue Number
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedIssue.issueNumber}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Department
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedIssue.department?.department}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Issue Date
                      </Typography>
                      <Typography variant="body1">
                        {new Date(selectedIssue.issueDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Purpose
                      </Typography>
                      <Typography variant="body1">
                        {selectedIssue.purpose}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Return Reason */}
          {selectedIssue && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Return Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Overall Return Reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Provide a general reason for this return"
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Additional Notes (Optional)"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any additional notes"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Returnable Items */}
          {selectedIssue && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Select Items to Return
                </Typography>

                {returnableLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : returnableItems.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Receipt
                      sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="body1" color="text.secondary">
                      No returnable items found for this issue
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">Select</TableCell>
                          <TableCell>Item</TableCell>
                          <TableCell align="center">Issued</TableCell>
                          <TableCell align="center">Returnable</TableCell>
                          <TableCell align="center">Batch</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {returnableItems.map((returnableItem) => {
                          const selected = isItemSelected(
                            returnableItem.originalItemId
                          );

                          return (
                            <TableRow key={returnableItem.issueItemId} hover>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selected}
                                  onChange={(e) =>
                                    handleItemSelect(
                                      returnableItem,
                                      e.target.checked
                                    )
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  {returnableItem.item.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {returnableItem.item.brand} •{" "}
                                  {returnableItem.item.itemType}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">
                                  {returnableItem.issuedQuantity}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={returnableItem.remainingQuantity}
                                  color="primary"
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="caption" display="block">
                                  {returnableItem.batchStock.batchNumber}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Exp:{" "}
                                  {new Date(
                                    returnableItem.batchStock.expiryDate
                                  ).toLocaleDateString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}

          {/* Selected Items Configuration */}
          {selectedItems.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Configure Return Items
                </Typography>

                {selectedItems.map((item) => (
                  <Box
                    key={item.issueItem}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {item.itemName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.itemBrand} • Batch: {item.batchNumber}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Return Quantity"
                          value={item.quantity === 0 ? "" : item.quantity}
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? 0
                                : parseInt(e.target.value);
                            updateSelectedItem(
                              item.issueItem,
                              "quantity",
                              value
                            );
                          }}
                          onBlur={(e) => {
                            if (
                              e.target.value === "" ||
                              parseInt(e.target.value) < 1
                            ) {
                              updateSelectedItem(item.issueItem, "quantity", 1);
                            }
                          }}
                          InputProps={{
                            inputProps: { min: 1, max: item.maxQuantity },
                            endAdornment: (
                              <InputAdornment position="end">
                                / {item.maxQuantity}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          select
                          label="Reason"
                          value={item.reason}
                          onChange={(e) =>
                            updateSelectedItem(
                              item.issueItem,
                              "reason",
                              e.target.value
                            )
                          }
                        >
                          <MenuItem value="defective">Defective</MenuItem>
                          <MenuItem value="excess_quantity">
                            Excess Quantity
                          </MenuItem>
                          <MenuItem value="wrong_item">Wrong Item</MenuItem>
                          <MenuItem value="project_cancelled">
                            Project Cancelled
                          </MenuItem>
                          <MenuItem value="expired">Expired</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Description (Optional)"
                          value={item.description}
                          onChange={(e) =>
                            updateSelectedItem(
                              item.issueItem,
                              "description",
                              e.target.value
                            )
                          }
                          placeholder="Provide additional details about this return"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormGroup row>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={item.restockable}
                                onChange={(e) =>
                                  updateSelectedItem(
                                    item.issueItem,
                                    "restockable",
                                    e.target.checked
                                  )
                                }
                                color="success"
                              />
                            }
                            label={
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Restore
                                  sx={{
                                    mr: 0.5,
                                    fontSize: 18,
                                    color: "success.main",
                                  }}
                                />
                                <Typography variant="body2">
                                  Restockable (can be returned to inventory)
                                </Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={item.disposalRequired}
                                onChange={(e) =>
                                  updateSelectedItem(
                                    item.issueItem,
                                    "disposalRequired",
                                    e.target.checked
                                  )
                                }
                                color="error"
                              />
                            }
                            label={
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Delete
                                  sx={{
                                    mr: 0.5,
                                    fontSize: 18,
                                    color: "error.main",
                                  }}
                                />
                                <Typography variant="body2">
                                  Disposal Required (cannot be restocked)
                                </Typography>
                              </Box>
                            }
                          />
                        </FormGroup>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {item.restockable &&
                            !item.disposalRequired &&
                            "✓ This item will be returned to inventory"}
                          {!item.restockable &&
                            item.disposalRequired &&
                            "⚠ This item will be marked for disposal"}
                          {!item.restockable &&
                            !item.disposalRequired &&
                            "❌ Please select a disposition"}
                          {item.restockable &&
                            item.disposalRequired &&
                            "❌ Cannot be both - please select only one"}
                        </Typography>

                        {/* {item.restockable && item.disposalRequired && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            Item cannot be both restockable and require
                            disposal. Please select only one option.
                          </Alert>
                        )} */}
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Column - Return Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 100 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Return Summary
              </Typography>

              {selectedIssue && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Original Issue
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedIssue.issueNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Department: {selectedIssue.department?.department}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Items Selected: {selectedItems.length}
                </Typography>

                {selectedItems.length > 0 && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">Subtotal:</Typography>
                      <Typography variant="body2">
                        ₹{subtotal.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">Tax (18%):</Typography>
                      <Typography variant="body2">₹{tax.toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        Total Amount:
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="primary.main"
                      >
                        ₹{totalAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {selectedItems.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      gutterBottom
                    >
                      Return Status:
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {selectedItems.some((item) => item.restockable) && (
                        <Chip
                          icon={<Restore />}
                          label={`${
                            selectedItems.filter((i) => i.restockable).length
                          } Restockable`}
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {selectedItems.some((item) => item.disposalRequired) && (
                        <Chip
                          icon={<Delete />}
                          label={`${
                            selectedItems.filter((i) => i.disposalRequired)
                              .length
                          } For Disposal`}
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={
                  !selectedIssue ||
                  selectedItems.length === 0 ||
                  !reason.trim() ||
                  loading ||
                  selectedItems.some(
                    (item) => item.restockable && item.disposalRequired
                  )
                }
                sx={{ py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Create Return"
                )}
              </Button>

              {selectedItems.some(
                (item) => item.restockable && item.disposalRequired
              ) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Some items have conflicting flags. Please review.
                </Alert>
              )}

              {selectedIssue && selectedItems.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Select items from the list above to create a return.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
